/**
 * Cross-tab impersonation seat sync.
 *
 * The impersonation seat (SA -> distributor or client, distributor -> view as
 * client) is stamped on the SERVER-side token row, and every tab of one login
 * shares that token. So a seat change in one tab -- a deep link that switches
 * automatically, the banner's exit, the SA Distributors list -- silently
 * re-seats every other open tab: they keep stale UI (no banner, the old
 * capability set) while their next API calls already run in the new seat.
 *
 * The tab that changes the seat announces it (the impersonation store does,
 * after every successful start/stop), and every OTHER tab reloads into its
 * role landing, which re-reads the banner and capabilities from the token.
 * The announcing tab does nothing here: it hard-navigates on its own.
 *
 * Transport: a BroadcastChannel when the browser has one, else a write to a
 * localStorage key that other tabs observe as a `storage` event (which never
 * fires in the writing tab). A channel message carries the sending page
 * load's random id, so the sender ignores its own. Neither carries any tenant
 * or account id or name, only "the seat changed".
 */

export const SEAT_CHANNEL = 'tf-impersonation-seat'
export const SEAT_STORAGE_KEY = 'tfImpersonationSeatChanged'
const MESSAGE_TYPE = 'seat-changed'

// This page load's id. A BroadcastChannel delivers to every OTHER channel
// object, including this tab's own listener, so a message carries its
// sender and a tab ignores its own (it hard-navigates on its own; reacting
// as well would race that navigation with a reload to "/").
const THIS_TAB = `${Date.now()}-${Math.random().toString(36).slice(2)}`

function defaultBroadcastChannel() {
    return typeof BroadcastChannel === 'function' ? BroadcastChannel : undefined
}

function defaultStorage() {
    try {
        return window.localStorage
    } catch (_e) {
        return null
    }
}

/**
 * Tell the user's other tabs that the impersonation seat changed. Never
 * throws: a failed announcement must not fail the switch itself.
 *
 * @param {object} [opts] injection seams for tests
 */
export function announceSeatChange({
    BroadcastChannelImpl = defaultBroadcastChannel(),
    storage = defaultStorage(),
    tabId = THIS_TAB,
} = {}) {
    try {
        if (BroadcastChannelImpl) {
            const channel = new BroadcastChannelImpl(SEAT_CHANNEL)
            channel.postMessage({ type: MESSAGE_TYPE, from: tabId })
            channel.close()
            return
        }
        // A storage event fires only when the value changes: make it unique.
        storage?.setItem(
            SEAT_STORAGE_KEY,
            `${Date.now()}-${Math.random().toString(36).slice(2)}`
        )
    } catch (_e) {
        // Best effort: the other tabs then catch up on their next load.
    }
}

/**
 * Listen for seat changes made in OTHER tabs. Reacts at most once per page
 * (a stop + start pair is one change, and the reaction reloads the page).
 *
 * @param {object} [opts]
 * @param {function} [opts.onSeatChange] the reaction; defaults to loading the
 *   role landing ("/") unless this tab is on the login page.
 * @returns {function} uninstall
 */
export function installImpersonationSeatSync({
    BroadcastChannelImpl = defaultBroadcastChannel(),
    target = typeof window !== 'undefined' ? window : null,
    location = typeof window !== 'undefined' ? window.location : null,
    onSeatChange = null,
    tabId = THIS_TAB,
} = {}) {
    const react =
        onSeatChange ||
        (() => {
            // A signed-out tab has nothing seated to refresh.
            if (!location || location.pathname === '/login') return
            location.assign('/')
        })
    let reacted = false
    const fire = () => {
        if (reacted) return
        reacted = true
        react()
    }

    if (BroadcastChannelImpl) {
        const channel = new BroadcastChannelImpl(SEAT_CHANNEL)
        channel.onmessage = (event) => {
            const data = event?.data
            if (data?.type === MESSAGE_TYPE && data.from !== tabId) fire()
        }
        return () => {
            channel.onmessage = null
            channel.close()
        }
    }

    const onStorage = (event) => {
        if (event?.key === SEAT_STORAGE_KEY && event.newValue) fire()
    }
    target?.addEventListener('storage', onStorage)
    return () => target?.removeEventListener('storage', onStorage)
}
